import { zodResolver } from '@hookform/resolvers/zod';
import { format, isToday, isTomorrow } from 'date-fns';
import { Car, Check, Package } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import { LawfulBasisAttestation } from '@/components/consent/LawfulBasisAttestation';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { TERMS_OF_SERVICE_VERSION } from '@/constants/legal';
import { KE_PHONE_PATTERN, normalizeKePhoneToE164 } from '@/lib/phone';
import {
  createDeliveryPass,
  createDomesticWorker,
  createRecurringPass,
  createTaxiPass,
  getGates,
  getPropertyConfig,
  recordConsent,
} from '@/services/api.client';
import type { DeliveryPassWithPin, TaxiPassWithPin } from '@/types';

/**
 * Fire-and-forget recordConsent helper used across the four pass-create
 * forms. KDPA / ToS §6 — the inviting user attests they have a lawful
 * basis to share the third party's contact details. Failure here is
 * logged to console but does not roll back the pass creation; the user
 * already saw the checkbox and the row will be retried on the next
 * pass-create attempt.
 */
function recordLawfulBasis(propertyId: string): void {
  void recordConsent({
    scope: 'visitor_data_lawful_basis',
    version: TERMS_OF_SERVICE_VERSION,
    granted: true,
    propertyId,
  }).catch((err) => {
    console.warn('Failed to record visitor-data lawful-basis attestation', err);
  });
}

export type PassCreateType = 'recurring' | 'domestic_worker' | 'delivery' | 'taxi' | 'quick_code';

const DIALOG_TITLE: Record<PassCreateType, string> = {
  recurring: 'Create Recurring Pass',
  domestic_worker: 'Add Domestic Worker',
  delivery: 'Create Delivery Pass',
  taxi: 'Book a Taxi',
  quick_code: 'Delivery or taxi code',
};

// ── Recurring / Domestic Worker form ────────────────────────────────────────

const recurringSchema = z
  .object({
    passType: z.enum(['recurring', 'contractor']),
    visitorName: z.string().min(2, 'Name is required'),
    visitorPhone: z
      .string()
      .min(9, 'Enter a valid phone number')
      .regex(KE_PHONE_PATTERN, 'Enter a valid Kenyan phone number (e.g. +254712345678)'),
    recurrencePattern: z.enum(['daily', 'weekdays', 'specific_days']),
    entryTimeStart: z.string().min(1, 'Start time required'),
    entryTimeEnd: z.string().min(1, 'End time required'),
    validFrom: z.string().min(1, 'Start date required'),
    validUntil: z.string().optional(),
    gateId: z.string().optional(),
    notes: z.string().max(200, 'Max 200 characters').optional(),
    // KDPA / ToS §6 — the inviting user attests they have a lawful basis
    // to share the visitor / domestic worker's contact details with the
    // Platform. Required.
    lawfulBasisAttested: z.boolean().refine((v) => v === true, {
      message: 'Please confirm you have asked them for permission',
    }),
  })
  .refine((d) => !d.validUntil || !d.validFrom || d.validUntil >= d.validFrom, {
    message: 'End date must be after start date',
    path: ['validUntil'],
  })
  // A contractor pass is a short, fixed job — the last day on site is required
  // so the pass can't be left open-ended.
  .refine((d) => d.passType !== 'contractor' || !!d.validUntil, {
    message: 'Pick the last day on site',
    path: ['validUntil'],
  });

type RecurringValues = z.infer<typeof recurringSchema>;

function RecurringPassForm({
  propertyId,
  isDomesticWorker,
  onDone,
}: {
  propertyId: string;
  isDomesticWorker: boolean;
  onDone: () => void;
}) {
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RecurringValues>({
    resolver: zodResolver(recurringSchema),
    defaultValues: {
      passType: 'recurring',
      recurrencePattern: 'weekdays',
      entryTimeStart: '08:00',
      entryTimeEnd: '18:00',
      lawfulBasisAttested: false as unknown as true,
    },
  });

  const lawfulBasisAttested = watch('lawfulBasisAttested');
  const selectedPassType = watch('passType');
  const isContractor = !isDomesticWorker && selectedPassType === 'contractor';

  // A contractor comes every day for the length of the job, so we don't ask
  // them to pick weekdays — we force a daily schedule and just collect the
  // first and last day on site.
  useEffect(() => {
    if (isContractor) {
      setValue('recurrencePattern', 'daily');
    }
  }, [isContractor, setValue]);

  const gatesQuery = useQuery({
    queryKey: ['gates', propertyId],
    queryFn: () => getGates(propertyId),
  });

  const mutation = useMutation({
    mutationFn: (values: RecurringValues) => {
      const body = {
        visitorName: values.visitorName,
        // Schema validated the shape; normalise to E.164 before sending.
        visitorPhone: normalizeKePhoneToE164(values.visitorPhone) ?? values.visitorPhone,
        recurrencePattern: values.recurrencePattern,
        entryTimeStart: values.entryTimeStart,
        entryTimeEnd: values.entryTimeEnd,
        validFrom: new Date(values.validFrom).toISOString(),
        passType: isDomesticWorker
          ? ('domestic_worker' as const)
          : (values.passType as 'recurring' | 'contractor'),
        ...(values.validUntil ? { validUntil: new Date(values.validUntil).toISOString() } : {}),
        ...(values.gateId ? { gateId: values.gateId } : {}),
        ...(values.notes ? { notes: values.notes } : {}),
      };
      return isDomesticWorker
        ? createDomesticWorker(propertyId, body)
        : createRecurringPass(propertyId, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: isDomesticWorker
          ? ['domestic-workers', propertyId]
          : ['recurring-passes', propertyId],
      });
      recordLawfulBasis(propertyId);
      toast.success(isDomesticWorker ? 'Domestic worker added' : 'Recurring pass created');
      onDone();
    },
    onError: (err) => {
      const status = (err as { status?: number }).status;
      if (status === 409) toast.error('A pass already exists for this visitor');
      else toast.error('Failed to create pass');
    },
  });

  const gateId = watch('gateId');

  return (
    <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)}>
      <FieldGroup className="py-2">
        {!isDomesticWorker && (
          <Field>
            <FieldLabel htmlFor="passType">Who is this for?</FieldLabel>
            <select
              id="passType"
              aria-label="Pass type"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              {...register('passType')}
            >
              <option value="recurring">Regular visitor — comes on a set weekly schedule</option>
              <option value="contractor">Contractor — comes daily for a short job</option>
            </select>
            <FieldError errors={[errors.passType]} />
          </Field>
        )}

        <Field>
          <FieldLabel htmlFor="visitorName">
            {isContractor ? 'Contractor / Lead Name' : 'Visitor Name'}
          </FieldLabel>
          <Input id="visitorName" placeholder="Jane Kamau" {...register('visitorName')} />
          <FieldError errors={[errors.visitorName]} />
        </Field>
        <Field>
          <FieldLabel htmlFor="visitorPhone">Phone</FieldLabel>
          <Input
            id="visitorPhone"
            type="tel"
            placeholder="+254 700 000 000"
            {...register('visitorPhone')}
          />
          <FieldError errors={[errors.visitorPhone]} />
        </Field>

        {!isContractor && (
          <Field>
            <FieldLabel htmlFor="recurrencePattern">Schedule</FieldLabel>
            <select
              id="recurrencePattern"
              aria-label="Recurrence pattern"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              {...register('recurrencePattern')}
            >
              <option value="weekdays">Weekdays (Mon–Fri)</option>
              <option value="daily">Daily</option>
              <option value="specific_days">Specific days</option>
            </select>
            <FieldError errors={[errors.recurrencePattern]} />
          </Field>
        )}

        {isContractor && (
          <p className="rounded-md border border-input bg-muted/40 px-3 py-2 text-xs text-[var(--text-secondary)]">
            This pass works every day between the first and last day on site. No need to pick
            individual days.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field>
            <FieldLabel htmlFor="entryTimeStart">Entry from</FieldLabel>
            <Input id="entryTimeStart" type="time" {...register('entryTimeStart')} />
            <FieldError errors={[errors.entryTimeStart]} />
          </Field>
          <Field>
            <FieldLabel htmlFor="entryTimeEnd">Entry until</FieldLabel>
            <Input id="entryTimeEnd" type="time" {...register('entryTimeEnd')} />
            <FieldError errors={[errors.entryTimeEnd]} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field>
            <FieldLabel htmlFor="validFrom">
              {isContractor ? 'First day on site' : 'Valid from'}
            </FieldLabel>
            <Input id="validFrom" type="date" {...register('validFrom')} />
            <FieldError errors={[errors.validFrom]} />
          </Field>
          <Field>
            <FieldLabel htmlFor="validUntil">
              {isContractor ? (
                'Last day on site'
              ) : (
                <>
                  Valid until{' '}
                  <span className="text-[var(--text-tertiary)] font-normal">(optional)</span>
                </>
              )}
            </FieldLabel>
            <Input id="validUntil" type="date" {...register('validUntil')} />
            <FieldError errors={[errors.validUntil]} />
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="gateId">
            Gate <span className="text-[var(--text-tertiary)] font-normal">(optional)</span>
          </FieldLabel>
          {gatesQuery.isLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <select
              id="gateId"
              aria-label="Gate"
              value={gateId ?? ''}
              onChange={(e) => setValue('gateId', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Any gate</option>
              {gatesQuery.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="notes">
            Notes <span className="text-[var(--text-tertiary)] font-normal">(optional)</span>
          </FieldLabel>
          <Textarea id="notes" rows={2} maxLength={200} {...register('notes')} />
        </Field>

        <LawfulBasisAttestation
          checked={!!lawfulBasisAttested}
          onChange={(next) =>
            setValue('lawfulBasisAttested', next ? (true as true) : (false as unknown as true), {
              shouldValidate: true,
            })
          }
          error={errors.lawfulBasisAttested?.message as string | undefined}
          subject={
            isDomesticWorker ? 'this worker' : isContractor ? 'this contractor' : 'this visitor'
          }
        />
      </FieldGroup>

      <DialogFooter className="mt-4">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={mutation.isPending || !lawfulBasisAttested}>
          {mutation.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ── Duration chip picker (shared by taxi and delivery) ───────────────────────

type ChipOption = { label: string; value: string };

function DurationChips({
  options,
  selected,
  onSelect,
}: {
  options: ChipOption[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onSelect(opt.value)}
          className={[
            'rounded-full px-4 py-1.5 text-sm font-medium transition-colors border',
            selected === opt.value
              ? 'text-[var(--text-inverse)] border-transparent'
              : 'border-[var(--bg-surface-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--text-secondary)]',
          ].join(' ')}
          style={selected === opt.value ? { background: 'var(--cta-primary-bg)' } : {}}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Success view (shared by taxi and delivery) ───────────────────────────────

function friendlyValidity(validUntil: string): string {
  const d = new Date(validUntil);
  const timeStr = format(d, 'HH:mm');
  if (isToday(d)) return `until ${timeStr} today`;
  if (isTomorrow(d)) return `until ${timeStr} tomorrow`;
  return `until ${format(d, 'dd MMM HH:mm')}`;
}

function PassSuccessView({
  pin,
  validUntil,
  passKind,
  propertyName,
  onDone,
}: {
  pin: string;
  validUntil: string;
  passKind: 'taxi' | 'delivery';
  propertyName: string;
  onDone: () => void;
}) {
  const validity = friendlyValidity(validUntil);
  const shareMsg =
    passKind === 'taxi'
      ? `Use code ${pin} at ${propertyName} Main Gate. Valid ${validity}. Show this at the gate.`
      : `Use code ${pin} at ${propertyName}. Valid ${validity}. Show this at the gate.`;

  const formattedPin = pin.split('').join(' ');

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  }

  return (
    <div className="flex flex-col items-center gap-5 py-4">
      <div
        className="flex size-12 items-center justify-center rounded-full"
        style={{ background: 'var(--status-green)', color: 'var(--text-inverse)' }}
      >
        <Check className="size-6" strokeWidth={2.5} />
      </div>

      <p className="text-base font-semibold text-[var(--text-primary)]">Your code is ready</p>

      <div
        className="rounded-xl border px-8 py-4 text-center"
        style={{ borderColor: 'var(--bg-surface-raised)', background: 'var(--bg-surface-raised)' }}
      >
        <p
          className="text-3xl font-mono font-bold tracking-widest text-[var(--text-primary)]"
          aria-label={`Access code: ${pin}`}
        >
          {formattedPin}
        </p>
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">Valid {validity}</p>
      </div>

      <div className="flex w-full flex-col gap-2">
        <Button className="w-full" onClick={() => void copyText(shareMsg)}>
          Copy share message
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-[var(--text-secondary)]"
          onClick={() => void copyText(pin)}
        >
          Copy code only
        </Button>
        <Button variant="outline" className="w-full" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ── Taxi form ───────────────────────────────────────────────────────────────

const TAXI_CHIPS: ChipOption[] = [
  { label: '30 min', value: '30' },
  { label: '1 hour', value: '60' },
  { label: '2 hours', value: '120' },
  { label: '4 hours', value: '240' },
  { label: 'Custom', value: 'custom' },
];

const taxiSchema = z.object({
  validityMinutes: z.number().int().min(15).max(480),
});

type TaxiValues = z.infer<typeof taxiSchema>;

function TaxiPassForm({
  propertyId,
  propertyName,
  onDone,
}: {
  propertyId: string;
  propertyName: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [selectedChip, setSelectedChip] = useState<string>('60');
  const [pinResult, setPinResult] = useState<TaxiPassWithPin | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TaxiValues>({
    resolver: zodResolver(taxiSchema),
    defaultValues: { validityMinutes: 60 },
  });

  function handleChipSelect(value: string) {
    setSelectedChip(value);
    if (value !== 'custom') {
      setValue('validityMinutes', parseInt(value, 10));
    }
  }

  const mutation = useMutation({
    mutationFn: (values: TaxiValues) =>
      createTaxiPass(propertyId, { validityMinutes: values.validityMinutes }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['taxi-passes', propertyId] });
      setPinResult(result);
    },
    onError: () => toast.error('Failed to create taxi pass'),
  });

  if (pinResult) {
    return (
      <PassSuccessView
        pin={pinResult.pin ?? '------'}
        validUntil={pinResult.validUntil}
        passKind="taxi"
        propertyName={propertyName}
        onDone={onDone}
      />
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)}>
      <FieldGroup className="py-2">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          How long should this code work?
        </p>
        <DurationChips options={TAXI_CHIPS} selected={selectedChip} onSelect={handleChipSelect} />
        {selectedChip === 'custom' && (
          <Field>
            <FieldLabel htmlFor="taxiCustomMinutes">Minutes (15–480)</FieldLabel>
            <Input
              id="taxiCustomMinutes"
              type="number"
              min={15}
              max={480}
              step={15}
              placeholder="e.g. 90"
              {...register('validityMinutes', { valueAsNumber: true })}
            />
            <FieldError errors={[errors.validityMinutes]} />
          </Field>
        )}
      </FieldGroup>

      <DialogFooter className="mt-4">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Create Code'}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ── Delivery form ───────────────────────────────────────────────────────────

const DELIVERY_CHIPS: ChipOption[] = [
  { label: '2 hours', value: '2h' },
  { label: '4 hours', value: '4h' },
  { label: '8 hours', value: '8h' },
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'Custom', value: 'custom' },
];

const deliverySchema = z.object({
  preset: z.enum(['2h', '4h', '8h', 'today', 'tomorrow', 'custom']),
  customHours: z.number().int().min(1).max(72).optional(),
});

type DeliveryValues = z.infer<typeof deliverySchema>;

function presetToValidUntil(preset: DeliveryValues['preset'], customHours?: number): string {
  const now = new Date();
  if (preset === '2h') {
    return new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();
  }
  if (preset === '4h') {
    return new Date(now.getTime() + 4 * 60 * 60 * 1000).toISOString();
  }
  if (preset === '8h') {
    return new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString();
  }
  if (preset === 'today') {
    const end = new Date(now);
    end.setHours(23, 59, 0, 0);
    return end.toISOString();
  }
  if (preset === 'tomorrow') {
    const end = new Date(now);
    end.setDate(end.getDate() + 1);
    end.setHours(23, 59, 0, 0);
    return end.toISOString();
  }
  // custom
  const hours = customHours ?? 1;
  return new Date(now.getTime() + hours * 60 * 60 * 1000).toISOString();
}

function DeliveryPassForm({
  propertyId,
  propertyName,
  onDone,
}: {
  propertyId: string;
  propertyName: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [selectedChip, setSelectedChip] = useState<string>('4h');
  const [pinResult, setPinResult] = useState<DeliveryPassWithPin | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DeliveryValues>({
    resolver: zodResolver(deliverySchema),
    defaultValues: { preset: '4h' },
  });

  function handleChipSelect(value: string) {
    setSelectedChip(value);
    setValue('preset', value as DeliveryValues['preset']);
  }

  const mutation = useMutation({
    mutationFn: (values: DeliveryValues) => {
      const validUntil = presetToValidUntil(values.preset, values.customHours);
      return createDeliveryPass(propertyId, { validUntil });
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['delivery-passes', propertyId] });
      setPinResult(result);
    },
    onError: () => toast.error('Failed to create delivery pass'),
  });

  if (pinResult) {
    return (
      <PassSuccessView
        pin={pinResult.pin ?? '------'}
        validUntil={pinResult.validUntil}
        passKind="delivery"
        propertyName={propertyName}
        onDone={onDone}
      />
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)}>
      <FieldGroup className="py-2">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          How long should this code work?
        </p>
        <DurationChips
          options={DELIVERY_CHIPS}
          selected={selectedChip}
          onSelect={handleChipSelect}
        />
        {selectedChip === 'custom' && (
          <Field>
            <FieldLabel htmlFor="deliveryCustomHours">Hours (1–72)</FieldLabel>
            <Input
              id="deliveryCustomHours"
              type="number"
              min={1}
              max={72}
              step={1}
              placeholder="e.g. 6"
              {...register('customHours', { valueAsNumber: true })}
            />
            <FieldError errors={[errors.customHours]} />
          </Field>
        )}
      </FieldGroup>

      <DialogFooter className="mt-4">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Create Code'}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ── Quick code form (delivery + taxi consolidated) ──────────────────────────

// Delivery and taxi codes are the same thing to a resident: a one-time PIN to
// share on WhatsApp. They differ only in how long the code lasts, so we present
// them under one entry point with a simple toggle instead of two separate tiles.

function quickToggleClass(active: boolean): string {
  return [
    'flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
    active
      ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm'
      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
  ].join(' ');
}

function QuickCodeForm({
  propertyId,
  propertyName,
  onDone,
}: {
  propertyId: string;
  propertyName: string;
  onDone: () => void;
}) {
  const [kind, setKind] = useState<'delivery' | 'taxi'>('delivery');

  return (
    <div className="py-1">
      <div
        className="mb-3 grid grid-cols-2 gap-1 rounded-lg p-1"
        style={{ background: 'var(--bg-surface-raised)' }}
        role="tablist"
        aria-label="Code type"
      >
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'delivery'}
          onClick={() => setKind('delivery')}
          className={quickToggleClass(kind === 'delivery')}
        >
          <Package className="size-4" aria-hidden="true" />
          Delivery
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'taxi'}
          onClick={() => setKind('taxi')}
          className={quickToggleClass(kind === 'taxi')}
        >
          <Car className="size-4" aria-hidden="true" />
          Taxi or ride
        </button>
      </div>

      {kind === 'delivery' ? (
        <DeliveryPassForm propertyId={propertyId} propertyName={propertyName} onDone={onDone} />
      ) : (
        <TaxiPassForm propertyId={propertyId} propertyName={propertyName} onDone={onDone} />
      )}
    </div>
  );
}

// ── Exported dialog ─────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  passType: PassCreateType;
}

export function PassCreateDialog({ open, onOpenChange, propertyId, passType }: Props) {
  const close = () => onOpenChange(false);

  const propertyQuery = useQuery({
    queryKey: ['property-config', propertyId],
    queryFn: () => getPropertyConfig(propertyId),
    staleTime: 10 * 60 * 1000,
    enabled: passType === 'taxi' || passType === 'delivery' || passType === 'quick_code',
  });

  const propertyName = propertyQuery.data?.name ?? 'the estate';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{DIALOG_TITLE[passType]}</DialogTitle>
        </DialogHeader>
        {passType === 'quick_code' && (
          <QuickCodeForm propertyId={propertyId} propertyName={propertyName} onDone={close} />
        )}
        {passType === 'delivery' && (
          <DeliveryPassForm propertyId={propertyId} propertyName={propertyName} onDone={close} />
        )}
        {passType === 'taxi' && (
          <TaxiPassForm propertyId={propertyId} propertyName={propertyName} onDone={close} />
        )}
        {(passType === 'recurring' || passType === 'domestic_worker') && (
          <RecurringPassForm
            propertyId={propertyId}
            isDomesticWorker={passType === 'domestic_worker'}
            onDone={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
